import {
  CycleCountConfig,
  CycleCountFrequency,
  CycleCountStatus,
} from '../models/cycle-count-config.model';

const ALLOWED_FREQUENCIES: CycleCountFrequency[] = ['Weekly', 'Monthly'];
const COUNT_SIZE_MIN = 10;
const COUNT_SIZE_MAX = 200;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates a cycle count frequency value.
 * Returns null if valid, or an error string if invalid.
 */
export function validateFrequency(
  value: string | null | undefined
): string | null {
  if (!value) {
    return `Frequency is required. Allowed values: ${ALLOWED_FREQUENCIES.join(', ')}.`;
  }
  if (!ALLOWED_FREQUENCIES.includes(value as CycleCountFrequency)) {
    return `Invalid frequency "${value}". Allowed values: ${ALLOWED_FREQUENCIES.join(', ')}.`;
  }
  return null;
}

/**
 * Validates a cycle count size value.
 * Returns null if valid, or an error string if invalid.
 */
export function validateCountSize(
  value: number | null | undefined
): string | null {
  if (value == null) {
    return `Count Size is required. Must be between ${COUNT_SIZE_MIN} and ${COUNT_SIZE_MAX}.`;
  }
  if (value < COUNT_SIZE_MIN || value > COUNT_SIZE_MAX) {
    return `Count Size must be between ${COUNT_SIZE_MIN} and ${COUNT_SIZE_MAX}.`;
  }
  return null;
}

/**
 * Validates a comma-separated report-recipient list.
 * Blank values are allowed, which disables variance-report email delivery.
 */
export function validateReportRecipients(
  value: string | null | undefined
): string | null {
  if (!value?.trim()) {
    return null;
  }

  const invalid = value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .find((entry) => !EMAIL_PATTERN.test(entry));

  if (invalid) {
    return `Invalid email address: ${invalid}. Use comma-separated email addresses.`;
  }

  return null;
}

/**
 * Parses the comma-separated string from the Admins recipient prompt into a
 * cleaned email list: each entry trimmed, blank segments (including a lone
 * whitespace entry or a trailing comma) dropped.
 *
 * An empty result is meaningful — it is the signal that the submission would
 * clear the admin list (see {@link adminRecipientEditClearsList}).
 */
export function parseAdminRecipientInput(input: string): string[] {
  return input
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * Returns true when saving `emails` would clear a warehouse's admin recipient
 * list — i.e. the parsed submission is empty AND active admins currently exist.
 *
 * Background (bug #30360): the web Admins prompt PUT the parsed list straight to
 * the API, so an empty entry silently wiped every admin recipient with no
 * confirmation. The guard fires only when there is actually something to lose;
 * clearing an already-empty list discards nothing, mirroring the #30359
 * "only warn when there is something to discard" philosophy.
 */
export function adminRecipientEditClearsList(
  emails: string[],
  existingActiveCount: number
): boolean {
  return emails.length === 0 && existingActiveCount > 0;
}

/**
 * Builds the confirmation-prompt text shown before an Admins edit that
 * {@link adminRecipientEditClearsList} flags, so the admin sees the concrete
 * consequence — every admin recipient for this warehouse is removed — before
 * confirming.
 */
export function buildAdminRecipientClearWarning(
  config: Pick<CycleCountConfig, 'warehouseName'>
): string {
  const warehouse = config.warehouseName?.trim()
    ? config.warehouseName.trim()
    : 'this warehouse';

  return (
    `This will clear every admin recipient for ${warehouse}. Variance-report ` +
    `emails will only go to the row's Report Recipients. Continue?`
  );
}

export function formatCycleCountStatus(status: CycleCountStatus | null): {
  label: string;
  color: string;
} {
  switch (status) {
    case 'Draft':
      return { label: 'Draft', color: '#c0a66a' };
    case 'InProgress':
      return { label: 'In Progress', color: '#0f326c' };
    case 'Complete':
      return { label: 'Complete', color: '#3d7f24' };
    case 'Adjusted':
      return { label: 'Adjusted', color: '#2f6f7d' };
    default:
      return { label: 'Not Started', color: '#9b9fa8' };
  }
}

/**
 * Computes the next scheduled date based on last run date and frequency.
 * Returns null if lastRunDate is null (warehouse has not yet been run).
 */
export function computeNextScheduledDate(
  lastRunDate: Date | null,
  frequency: CycleCountFrequency
): Date | null {
  if (!lastRunDate) return null;

  const next = new Date(lastRunDate);
  if (frequency === 'Weekly') {
    next.setUTCDate(next.getUTCDate() + 7);
  } else {
    next.setUTCMonth(next.getUTCMonth() + 1);
  }
  return next;
}

/**
 * Returns true when saving `edited` over `original` would discard the warehouse's
 * schedule (and orphan any open Draft session).
 *
 * Background (bug #30359): the API resets LastRunDate to null whenever Frequency
 * OR Count Size changes (CycleCountConfigRepository.Update). That nulls Next
 * Scheduled, re-enables Generate, and — because the Cycle Count button gates on
 * LastRunDate — makes an open Draft session unreachable from the row. Editing
 * recipients alone does not reset (the documented asymmetry), so it never warns.
 *
 * The guard fires only when there is actually something to lose: a config change
 * AND an existing run (LastRunDate present). A never-run warehouse has a null
 * LastRunDate that the reset leaves null, so there is nothing to discard.
 */
export function cycleCountConfigEditDiscardsSchedule(
  original: Pick<CycleCountConfig, 'frequency' | 'countSize' | 'lastRunDate'>,
  edited: Pick<CycleCountConfig, 'frequency' | 'countSize'>
): boolean {
  const configChanged =
    original.frequency !== edited.frequency ||
    original.countSize !== edited.countSize;

  return configChanged && original.lastRunDate != null;
}

/**
 * Builds the confirmation-prompt text shown before an edit that
 * {@link cycleCountConfigEditDiscardsSchedule} flags. When an open Draft session
 * exists the copy warns it becomes unreachable; otherwise it warns only that the
 * schedule (Last Run / Next Scheduled) is cleared.
 */
export function buildCycleCountConfigEditWarning(
  config: Pick<CycleCountConfig, 'warehouseName' | 'cycleCountStatus'>
): string {
  const warehouse = config.warehouseName?.trim()
    ? config.warehouseName.trim()
    : 'this warehouse';

  if (config.cycleCountStatus === 'Draft') {
    return (
      `Changing Frequency or Count Size clears the schedule for ${warehouse} and ` +
      `orphans its open Draft cycle count — the in-progress count will be unreachable ` +
      `from this screen. Continue?`
    );
  }

  return (
    `Changing Frequency or Count Size clears the Last Run date and Next Scheduled ` +
    `for ${warehouse}. Continue?`
  );
}

/**
 * The three ways a Count Size edit can be resolved (#30580):
 * - `save`: nothing to discard (never-run) or no config change — save straight through.
 * - `discard-warn`: the edit resets the schedule but is NOT a pure grow — show the existing
 *   {@link buildCycleCountConfigEditWarning} confirmation (discard-only).
 * - `grow-or-discard`: an open Draft/InProgress count exists and the Count Size was *increased*
 *   with Frequency unchanged — offer the admin a third path: add the extra items to the existing
 *   count instead of discarding it.
 */
export type CountSizeEditAction = 'save' | 'discard-warn' | 'grow-or-discard';

/**
 * Classifies a Count Size edit so the admin UI can offer the right prompt (#30580). "Add items to
 * the existing count" is only offered when it can actually be honored: an open Draft/InProgress count
 * exists AND the size strictly increased AND Frequency is unchanged (a frequency change resets the
 * schedule regardless, so it is not a clean grow). Everything else keeps the pre-existing behavior —
 * a plain save when nothing is discarded, or the discard-only warning otherwise.
 */
export function resolveCountSizeEditAction(
  original: Pick<
    CycleCountConfig,
    | 'frequency'
    | 'countSize'
    | 'lastRunDate'
    | 'cycleCountId'
    | 'cycleCountStatus'
  >,
  edited: Pick<CycleCountConfig, 'frequency' | 'countSize'>
): CountSizeEditAction {
  if (!cycleCountConfigEditDiscardsSchedule(original, edited)) return 'save';

  const hasOpenCount =
    !!original.cycleCountId &&
    (original.cycleCountStatus === 'Draft' ||
      original.cycleCountStatus === 'InProgress');
  const isPureSizeIncrease =
    original.frequency === edited.frequency &&
    edited.countSize > original.countSize;

  return hasOpenCount && isPureSizeIncrease
    ? 'grow-or-discard'
    : 'discard-warn';
}

/**
 * Builds the prompt shown for a {@link resolveCountSizeEditAction} `grow-or-discard` edit, stating the
 * concrete choice: add items to the warehouse's open count to grow it to the new size, or discard it
 * and start a new count.
 */
export function buildGrowExistingCountPrompt(
  config: Pick<CycleCountConfig, 'warehouseName'>,
  newCountSize: number
): string {
  const warehouse = config.warehouseName?.trim()
    ? config.warehouseName.trim()
    : 'this warehouse';

  return (
    `${warehouse} has an open cycle count. Add more items to grow it to ${newCountSize} ` +
    `items, or discard it and start a new count?`
  );
}
